import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SubmitHandler, useForm } from 'react-hook-form';
import { schema } from './schema';
import { yupResolver } from '@hookform/resolvers/yup';
import { useLoginWithEmailService } from 'app/utils/services/authService';
import useRedirectWithFormId from 'app/utils/customHooks/useRedirectWithFormId';
import { AsanImzaFormFields } from 'app/constants/types';
import logger from 'app/utils/logger';

const useLoginForm = (tabNumber: number) => {
  // const { tabNumber } = props;
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [firstInput, setFirstInput] = useState<string | undefined>('');
  const [secondInput, setSecondInput] = useState<string | undefined>('');
  const navigate = useNavigate();

  const {
    register,
    unregister,
    handleSubmit,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const { isLoadingLogWithEmail, dataLogWithEmail, loginMutation } = useLoginWithEmailService();
  const { handleRedirect } = useRedirectWithFormId();

  useEffect(() => {
    setFirstInput('');
    setSecondInput('');
    clearErrors('email');
    clearErrors('password');
    clearErrors('asan_imza_phone');
    clearErrors('asan_imza_id');
    clearErrors('asan_imza_id');
    unregister('email');
    unregister('password');
    unregister('asan_imza_phone');
    unregister('asan_imza_id');
  }, [tabNumber]);

  useEffect(() => {
    if (dataLogWithEmail !== undefined) {
      handleRedirect({ ...dataLogWithEmail, email: firstInput, tab: tabNumber });
    }
  }, [dataLogWithEmail]);

  const handleOnChangeAsanPhone = (event: any) => {
    const inputValue = event.target.value;
    const cleanedValue = inputValue.replace(/[^\d]/g, '');

    let formattedString = '';

    if (cleanedValue.length <= 2) {
      formattedString = cleanedValue;
    } else if (cleanedValue.length <= 5) {
      formattedString = `(${cleanedValue.slice(0, 2)}) ${cleanedValue.slice(2)}`;
    } else if (cleanedValue.length <= 7) {
      formattedString = `(${cleanedValue.slice(0, 2)}) ${cleanedValue.slice(2, 5)} ${cleanedValue.slice(5, 7)}`;
    } else {
      formattedString = `(${cleanedValue.slice(0, 2)}) ${cleanedValue.slice(2, 5)} ${cleanedValue.slice(
        5,
        7
      )} ${cleanedValue.slice(7, 9)}`;
    }
    // const pattern = /^(\d{2})(\d{3})(\d{2})(\d{2})$/;
    // const replacement = '($1) $2 $3 $4';
    //
    // const formattedString = inputValue
    //   .replace(/[a-zA-Z]/g, '')
    //   .replace(pattern, replacement)
    //   .trim();

    setFirstInput(formattedString);
  };

  const handleOnChangeAsanID = (event: any) => {
    const inputValue = event.target.value;
    const formattedString = inputValue.replace(/[a-zA-Z]/g, '').trim();

    setSecondInput(formattedString);
  };

  const handleChangeFirstInput = (event: any) => {
    setFirstInput(event.target.value);
  };

  const handleChangeSecondInput = (event: any) => {
    setSecondInput(event.target.value);
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleOpenRecoveryPage = () => {
    loginMutation({ form_id: 'recovery_password' });
  };

  const handleOnSubmit = (data: any): SubmitHandler<AsanImzaFormFields> => {
    if (tabNumber === 0) {
      loginMutation({ ...data, password: btoa(data.password), totp: false });
    }

    if (tabNumber === 1) {
      loginMutation({
        ...data,
        asan_imza_phone: `994${data.asan_imza_phone.replace(/\s+/g, '').replace(/\(/g, '').replace(/\)/g, '')}`,
      });
    }

    if (tabNumber === 2) {
      loginMutation({ ...data, password: btoa(data.password), totp: true });
    }

    return;
  };

  const handleSubmitForm = handleSubmit(handleOnSubmit);

  return {
    t,
    showPassword,
    firstInput,
    secondInput,
    errors,
    register,
    isLoadingLogWithEmail,
    dataLogWithEmail,
    handleSubmitForm,
    navigate,
    handleOnChangeAsanID,
    handleOpenRecoveryPage,
    handleOnChangeAsanPhone,
    handleChangeFirstInput,
    handleChangeSecondInput,
    handleClickShowPassword,
    handleMouseDownPassword,
  };
};

export default useLoginForm;
